import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import dataStore from '../../../store/dataStore.ts'
import { IPet } from '../../../interfaces/types.ts'
import { deviceFromPet, getAge } from '../../../helpers/dataHelpers.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen'
import { faPaw } from '@fortawesome/free-solid-svg-icons/faPaw'
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell'
import { faDog } from '@fortawesome/free-solid-svg-icons/faDog'
import { faCat } from '@fortawesome/free-solid-svg-icons/faCat'
import { faHorse } from '@fortawesome/free-solid-svg-icons/faHorse'
import { faDove } from '@fortawesome/free-solid-svg-icons/faDove'
import { faFish } from '@fortawesome/free-solid-svg-icons/faFish'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { PopupPortal } from '../../../components/common/Popup/PopupPortal.tsx'
import { PetForm } from '../PetForm.tsx'
import { RemovePetButton } from '../../../components/common/buttons/RemovePetButton.tsx'
import { AlertPreferences } from '../../../components/common/AlertPreferences/AlertPreferences.tsx'

const SPECIES_ICONS: Record<string, IconDefinition> = {
    dog: faDog,
    cat: faCat,
    horse: faHorse,
    bird: faDove,
    fish: faFish,
}

export const PetListDesktop: React.FC = () => {
    const pets = dataStore(s => s.pets).sort((a, b) => a.petName > b.petName ? 1 : -1)
    const species = dataStore(s => s.species)
    const breeds = dataStore(s => s.breeds)
    const account = dataStore(s => s.account)
    const navigate = useNavigate()

    const [editingPet, setEditingPet] = React.useState<IPet | null>(null)
    const [isNew, setIsNew] = React.useState(false)
    const [alertPet, setAlertPet] = React.useState<IPet | null>(null)

    const handleEdit = (pet: IPet) => {
        setEditingPet(pet)
        setIsNew(false)
    }

    const handleAddNew = () => {
        setEditingPet(null)
        setIsNew(true)
    }

    const handleClose = () => {
        setEditingPet(null)
        setIsNew(false)
    }

    const imperial = account?.homeAddress?.country?.toLowerCase() === 'usa'
        || account?.homeAddress?.country?.toLowerCase() === 'us'

    const emptyState = (
        <div className={'flex flex-col items-center gap-4 py-12 text-gray-400'}>
            <FontAwesomeIcon icon={faPaw} size={'3x'} />
            <div className={'text-lg'}>No pets yet</div>
            <div className={'text-sm opacity-70'}>Add a pet to get started</div>
        </div>
    )

    const petCards = pets.map((pet: IPet) => {
        const device = deviceFromPet(pet)
        const speciesRecord = species.find(s => s._id === pet.species)
        const breedRecord = breeds.find(b => b._id === pet.breed)
        const age = pet.birthDate ? getAge(pet.birthDate, true) : ''
        const speciesKey = speciesRecord?.speciesName?.toLowerCase() || ''
        const speciesIcon = SPECIES_ICONS[speciesKey]

        return (
            <div key={pet._id}
                 className={'bg-primary text-primary-content rounded-xl p-4 flex gap-4 items-center overflow-hidden'}>
                {/* Pet avatar — matches dashboard triple-border style */}
                <div className={'shrink-0'}>
                    <div className={'rounded-full border-2 border-white'}>
                        <div className={'rounded-full border-4'} style={{ borderColor: pet.iconColor || '#ccc' }}>
                            <div className={'rounded-full border-2 border-white'}>
                                <img
                                    src={pet.imageURL}
                                    alt={pet.petName}
                                    className={'w-14 h-14 rounded-full object-cover'}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pet info */}
                <div className={'flex-1 min-w-0'}>
                    {/* Name row */}
                    <div className={'flex items-center justify-between gap-2'}>
                        <div className={'text-xl font-bold truncate'}>{pet.petName}</div>
                        <div className={'flex items-center gap-2 shrink-0'}>
                            {device && (
                                <span className={'text-xs opacity-50'}>{device.deviceName}</span>
                            )}
                            <RemovePetButton pet={pet} iconOnly className={'text-primary-content'} />
                            {device && (
                                <button
                                    className={'btn btn-ghost btn-sm btn-circle text-primary-content'}
                                    onClick={() => setAlertPet(pet)}
                                    title={'Alert preferences'}
                                >
                                    <FontAwesomeIcon icon={faBell} />
                                </button>
                            )}
                            <button
                                className={'btn btn-ghost btn-sm btn-circle text-primary-content'}
                                onClick={() => handleEdit(pet)}
                            >
                                <FontAwesomeIcon icon={faPen} />
                            </button>
                        </div>
                    </div>

                    {/* Detail columns */}
                    <div className={'grid grid-cols-3 gap-4 mt-2 text-sm'}>
                        {/* Column 1: Species icon + breed */}
                        <div className={'flex flex-col items-start gap-0.5'}>
                            {speciesIcon ? (
                                <FontAwesomeIcon icon={speciesIcon} className={'text-lg opacity-80'} />
                            ) : (
                                <span className={'text-xs font-semibold uppercase tracking-wide opacity-80'}>
                                    {speciesRecord?.speciesName || '—'}
                                </span>
                            )}
                            {breedRecord && (
                                <span className={'opacity-70 text-xs truncate max-w-full'}>{breedRecord.breedName}</span>
                            )}
                        </div>

                        {/* Column 2: Age */}
                        <div className={'flex flex-col items-start gap-0.5'}>
                            <span className={'text-xs uppercase tracking-wide opacity-60'}>Age</span>
                            <span className={'opacity-90'}>{age || '—'}</span>
                        </div>

                        {/* Column 3: Weight */}
                        <div className={'flex flex-col items-start gap-0.5'}>
                            <span className={'text-xs uppercase tracking-wide opacity-60'}>Weight</span>
                            <span className={'opacity-90'}>
                                {pet.petWeight ? `${pet.petWeight} ${imperial ? 'lbs' : 'kg'}` : '—'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        )
    })

    const showForm = editingPet || isNew

    return (
        <div className={'flex flex-col gap-6 w-full max-w-2xl mx-auto pb-6 px-4 max-h-[90vh] overflow-y-auto'}>
            {/* Header */}
            <div className={'sticky top-0 z-10 flex items-start justify-between bg-base-100 pt-6 pb-2'}>
                <div>
                    <h1 className={'text-2xl font-bold text-primary m-0'}>Your Pets</h1>
                    <p className={'text-sm text-gray-500 mt-1'}>Manage your pets and their profiles</p>
                </div>
                <div className={'flex items-center gap-2'}>
                    <button className={'btn btn-secondary btn-sm gap-2'} onClick={handleAddNew}>
                        <FontAwesomeIcon icon={faPaw} />
                        Add Pet
                    </button>
                    <button className={'btn btn-ghost btn-sm text-primary'} onClick={() => navigate('/map')}>
                        Close
                    </button>
                </div>
            </div>

            {/* Pet cards */}
            {pets.length ? (
                <div className={'flex flex-col gap-3'}>
                    {petCards}
                </div>
            ) : emptyState}

            {/* Edit/Add form in large popup */}
            <PopupPortal open={!!showForm}>
                <div className={'w-full max-h-[90vh] overflow-y-auto'}>
                    <PetForm
                        key={isNew ? 'new' : editingPet?._id}
                        init={isNew ? null : editingPet}
                        onClose={handleClose}
                        hideSelector
                        hideDelete
                    />
                </div>
            </PopupPortal>

            {/* Alert preferences popup */}
            {alertPet && (
                <AlertPreferences
                    open={!!alertPet}
                    setOpen={(o) => { if (!o) setAlertPet(null) }}
                    deviceId={deviceFromPet(alertPet)?._id || ''}
                    petName={alertPet.petName}
                />
            )}
        </div>
    )
}
